import { useRef } from "react";
import { Alert, Badge, Button, Card, Col, Row, Stack } from "react-bootstrap";
import CameraArrow from "./CameraArrow";
import { parseRoles } from "../helpers/cameraRoles";
import {
  useCameraImage,
  useInViewOnce,
  CAMERA_IMAGE_KINDS,
  IMAGE_STATUS,
} from "../helpers/useCameraImage";

const QA_BADGE = {
  true: { variant: "success", label: "QA OK" },
  false: { variant: "danger", label: "QA failed" },
  pending: { variant: "secondary", label: "QA pending" },
};

export const qaStateOf = (camera) =>
  camera.qa_check === true || camera.qa_check === false
    ? String(camera.qa_check)
    : "pending";

const ReviewImage = ({ image, alt, loadingText, errorText }) => (
  <div
    className="d-flex align-items-center justify-content-center bg-dark-subtle rounded"
    style={{ minHeight: 160 }}
  >
    {image.status === IMAGE_STATUS.READY ? (
      <img src={image.url} alt={alt} className="img-fluid rounded" />
    ) : image.status === IMAGE_STATUS.ERROR ? (
      <Alert variant="danger" className="m-2 py-1 px-2 small">
        {errorText}
      </Alert>
    ) : (
      <span className="text-muted small">{loadingText}</span>
    )}
  </div>
);

// One camera in the review list: 3D preview and deployment snapshot side by
// side, plus a Yes/No QA decision. Images are only fetched once the card has
// scrolled near the viewport so a store with many cameras doesn't fire
// hundreds of image requests on mount.
const CameraReviewCard = ({ camera, deploymentId, onQaCheck, disabled }) => {
  const cardRef = useRef(null);
  const inView = useInViewOnce(cardRef);

  const previewImage = useCameraImage(
    deploymentId,
    camera.camera_id,
    CAMERA_IMAGE_KINDS.PREVIEW,
    inView,
  );
  const deploymentImage = useCameraImage(
    deploymentId,
    camera.camera_id,
    CAMERA_IMAGE_KINDS.DEPLOYMENT,
    inView,
  );

  const qaState = qaStateOf(camera);
  const badge = QA_BADGE[qaState];
  const title =
    camera.backend_id !== null && camera.backend_id >= 0
      ? `Camera ${camera.backend_id}`
      : `Camera (synthetic ${camera.synthetic_id})`;

  return (
    <Card ref={cardRef} className="mb-3 shadow-sm" data-testid={`camera-card-${camera.camera_id}`}>
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <Stack direction="horizontal" gap={2}>
          <span className="fw-bold">{title}</span>
          <Badge bg={badge.variant}>{badge.label}</Badge>
        </Stack>
        <Stack direction="horizontal" gap={2}>
          <Button
            size="sm"
            variant={qaState === "false" ? "danger" : "outline-danger"}
            disabled={disabled}
            onClick={() => onQaCheck(camera.camera_id, false)}
          >
            No
          </Button>
          <Button
            size="sm"
            variant={qaState === "true" ? "success" : "outline-success"}
            disabled={disabled}
            onClick={() => onQaCheck(camera.camera_id, true)}
          >
            Yes
          </Button>
        </Stack>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col xs={12} md={6}>
            <div className="small text-muted mb-1">3D preview</div>
            <ReviewImage
              image={previewImage}
              alt={`${title} preview`}
              loadingText="Fetching 3D preview..."
              errorText="Could not fetch 3D preview!"
            />
          </Col>
          <Col xs={12} md={6}>
            <div className="small text-muted mb-1">Deployment snapshot</div>
            <ReviewImage
              image={deploymentImage}
              alt={`${title} deployment snapshot`}
              loadingText="Fetching camera snapshot..."
              errorText="Could not fetch camera snapshot!"
            />
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="small text-muted d-flex flex-wrap gap-3 align-items-center">
        <span>Synthetic Id: {camera.synthetic_id}</span>
        <span>Lens: {camera.lens}</span>
        <span>Roles: {parseRoles(camera.roles).join(", ")}</span>
        <span>Height: {camera.height}</span>
        <span>Pitch: {camera.pitch}&deg;</span>
        {!camera.roles?.includes("top_view") && (
          <Stack direction="horizontal" gap={1}>
            <span>Yaw: {camera.yaw}&deg;</span>
            <CameraArrow yaw={camera.yaw} />
          </Stack>
        )}
        {camera.ip_address && <span>IP: {camera.ip_address}</span>}
      </Card.Footer>
    </Card>
  );
};

export default CameraReviewCard;
